import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ListEmployeeProfilesQuery,
  UpdateEmployeeProfileSelfDTO,
  UpsertEmployeeProfileDTO,
} from "@/modules/hr/employee-profile/domain/types";
import { employeeProfileApi } from "./client";

export const employeeProfileKeys = {
  list: (query: ListEmployeeProfilesQuery) =>
    ["hr", "employee-profile", "list", query] as const,
  detail: (userId: string) =>
    ["hr", "employee-profile", "detail", userId] as const,
  self: ["hr", "employee-profile", "self"] as const,
};

export function useEmployeeProfileListQuery(query: ListEmployeeProfilesQuery) {
  return useQuery({
    queryKey: employeeProfileKeys.list(query),
    queryFn: () => employeeProfileApi.list(query),
  });
}

export function useEmployeeProfileDetailQuery(userId: string) {
  return useQuery({
    queryKey: employeeProfileKeys.detail(userId),
    queryFn: () => employeeProfileApi.getById(userId),
    enabled: !!userId,
  });
}

export function useEmployeeProfileSelfQuery() {
  return useQuery({
    queryKey: employeeProfileKeys.self,
    queryFn: () => employeeProfileApi.getSelf(),
  });
}

export function useUpsertEmployeeProfileMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: Omit<UpsertEmployeeProfileDTO, "userId" | "actorId">) =>
      employeeProfileApi.upsert(userId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: employeeProfileKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: ["hr", "employee-profile", "list"] });
    },
  });
}

export function useUpdateEmployeeProfileSelfMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateEmployeeProfileSelfDTO) =>
      employeeProfileApi.updateSelf(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: employeeProfileKeys.self });
    },
  });
}
